'use client'

import { useEffect, useState } from 'react'
import { noop } from 'lodash'

import { ErrorItem, ResponseCode } from '@marketplace-web/core-api/api-client-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { UiState } from '@marketplace-web/shared/ui-state'
import {
  addAddress,
  AddressEntryType,
  AddressField,
  AddressFieldTranslationModel,
  AddressModel,
  transformAddress,
} from '@marketplace-web/address/address-data'

import { clickEvent, viewScreenEvent } from '_libs/common/event-tracker/events'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import { Screen } from 'constants/tracking/screens'

import AddressForm from '../AddressForm'
import { AddressScreenTrackingType } from '../../constants'

type Props = {
  address?: AddressModel | null
  screenTrackingType?: AddressScreenTrackingType
  onChange: (address: AddressModel) => void
  onCancel: () => void
  onSubmitClick?: () => void
}

const BillingAddress = ({
  address: defaultAddress,
  screenTrackingType,
  onChange,
  onCancel,
  onSubmitClick = noop,
}: Props) => {
  const translate = useTranslate('billing_address')
  const { track } = useTracking()

  const [submitState, setSubmitState] = useState<UiState>(UiState.Idle)
  const [errors, setErrors] = useState<Array<ErrorItem>>([])

  useEffect(() => {
    track(viewScreenEvent({ screen: Screen.BillingAddressUpdate }))
  }, [track])

  const trackSubmit = ({ valid }: { valid: boolean }) => {
    const trackingEvent = clickEvent({
      screen: Screen.BillingAddressUpdate,
      target: ClickableElement.SaveBillingAddress,
      targetDetails: JSON.stringify({
        valid,
        type: screenTrackingType,
      }),
    })

    track(trackingEvent)
  }

  const handleSubmit = async (address: AddressModel, isFormValid: boolean) => {
    if (!isFormValid) return

    onSubmitClick()

    setSubmitState(UiState.Pending)
    setErrors([])

    const response = await addAddress({
      address: { ...address, entryType: AddressEntryType.Billing },
    })

    if ('errors' in response) {
      trackSubmit({ valid: false })

      const { message, code } = response

      let generalFieldErrors: Array<ErrorItem> = []

      if (message && code === ResponseCode.CountryChangeNotAllowed) {
        generalFieldErrors = [
          {
            field: AddressField.Country,
            value: response.message,
          },
        ]
      }

      setSubmitState(UiState.Failure)
      setErrors([...response.errors, ...generalFieldErrors])

      return
    }

    trackSubmit({ valid: true })

    setSubmitState(UiState.Success)

    onChange(transformAddress(response.user_address))
  }

  const getTranslations = (): AddressFieldTranslationModel => ({
    name: {
      title: translate('name.title'),
      placeholder: translate('name.placeholder'),
    },
    country: {
      title: translate('country.title'),
    },
    line1: {
      title: translate('line1.title'),
      placeholder: translate('line1.placeholder'),
    },
    line2: {
      title: translate('line2.title'),
      placeholder: translate('line2.placeholder'),
    },
    city: {
      title: translate('city.title'),
      placeholder: translate('city.placeholder'),
    },
    postalCode: {
      title: translate('postal_code.title'),
      placeholder: translate('postal_code.placeholder'),
    },
    buttons: {
      continue: translate('buttons.continue'),
      cancel: translate('buttons.cancel'),
    },
  })

  const renderAddressForm = () => (
    <AddressForm
      defaultValues={defaultAddress}
      errors={errors}
      isSubmitPending={submitState === UiState.Pending}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      translations={getTranslations()}
    />
  )

  return renderAddressForm()
}

export default BillingAddress
